import { ReactNode, useId } from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { InputProps } from '@/types/form/input-props';

type CardRadioInputOption = {
  label: string;
  value: string;
  sublabel?: string;
  description?: string;
  icon?: ReactNode;
  /** Shown inside the card while the option is selected, e.g. the fields the option needs */
  content?: ReactNode;
};

type CardRadioInputProps = InputProps<string> & { options: CardRadioInputOption[] };

export function CardRadioInput({ value, onChange, options }: CardRadioInputProps) {
  return (
    <RadioGroup className="gap-2" defaultValue={value} onValueChange={onChange} value={value}>
      {options.map((option) => (
        <CardRadioOption {...option} selected={option.value === value} key={option.value} />
      ))}
    </RadioGroup>
  );
}

function CardRadioOption({
  value,
  icon,
  label,
  sublabel,
  description,
  content,
  selected,
}: CardRadioInputOption & { selected: boolean }) {
  const id = useId();
  return (
    <div className="relative flex w-full items-start gap-2 rounded-md border border-input p-4 shadow-xs outline-none has-data-[state=checked]:border-primary/50">
      <RadioGroupItem
        value={value}
        id={id}
        aria-describedby={`${id}-description`}
        className="order-1 after:absolute after:inset-0"
      />
      <div className="flex grow items-start gap-3">
        {/* Wide content in the selected card would otherwise squeeze the icon */}
        {icon && <span className="flex shrink-0">{icon}</span>}
        <div className="grid min-w-0 grow gap-2">
          <Label htmlFor={id}>
            {label}{' '}
            {sublabel && (
              <span className="text-xs leading-[inherit] font-normal text-muted-foreground">{sublabel}</span>
            )}
          </Label>
          {description && (
            <p id={`${id}-description`} className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
          {/* Above the radio's full-card click area, so the content can be used */}
          {selected && content && <div className="relative z-10 pt-2">{content}</div>}
        </div>
      </div>
    </div>
  );
}
